import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import NewsCard from "../components/NewsCard";
import Reveal from "../components/Reveal";
import { useLang, DISTRICTS } from "../lib/i18n";
import { districtPhoto } from "../lib/rajasthanPhotos";

export default function DistrictPage() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const district = DISTRICTS.find((d) => d.id === id);
  const [articles, setArticles] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setArticles(null);
    api
      .get(`/districts/${id}`)
      .then((r) => {
        setArticles(r.data.articles);
        setTotal(r.data.total);
      })
      .catch(() => setArticles([]));
  }, [id]);

  if (!district) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center">{t.noResults}</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="district-page">
      <div className="relative overflow-hidden border-2 border-ink mb-8" data-testid="district-banner">
        <img
          src={districtPhoto(id)}
          alt={lang === "hi" ? district.hi : district.en}
          className="w-full aspect-[16/6] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
          <Link
            to="/districts"
            data-testid="district-back"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold-light hover:text-gold transition-colors"
          >
            ← {lang === "hi" ? "राजस्थान के ज़िले" : "Rajasthan districts"}
          </Link>
          <h1 data-testid="district-title" className="font-hindi text-4xl md:text-6xl text-paper mt-2 drop-shadow">
            {lang === "hi" ? district.hi : district.en}
          </h1>
          <p className="font-mono text-[11px] text-gold-light/90 mt-1.5">
            {total} {t.stories}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-10" data-testid="district-chips">
        {DISTRICTS.map((d) => (
          <Link
            key={d.id}
            to={`/district/${d.id}`}
            data-testid={`chip-${d.id}`}
            className={`px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
              d.id === id
                ? "bg-deepgreen text-paper border-deepgreen"
                : "border-ink/25 text-ink hover:border-deepgreen hover:text-deepgreen"
            }`}
          >
            {lang === "hi" ? d.hi : d.en}
          </Link>
        ))}
      </div>

      {!articles ? (
        <div className="text-center font-mono text-sm text-muted-foreground py-10">…</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20" data-testid="district-empty">
          <p className="text-muted-foreground">
            {lang === "hi"
              ? `${district.hi} की अभी कोई ताज़ा ख़बर नहीं — auto-refresh पर आते ही यहाँ दिखेंगी`
              : `No fresh stories from ${district.en} yet — they'll appear here on the next auto-refresh`}
          </p>
        </div>
      ) : (
        <>
          <Reveal>
            <div className="grid md:grid-cols-2 gap-8 mb-10 pb-10 border-b-2 border-ink">
              <NewsCard article={articles[0]} variant="standard" />
              <div>
                {articles.slice(1, 5).map((a) => (
                  <NewsCard key={a.id} article={a} variant="list" />
                ))}
              </div>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {articles.slice(5).map((a, i) => (
              <Reveal key={a.id} delay={Math.min(i * 0.06, 0.4)}>
                <NewsCard article={a} variant="standard" />
              </Reveal>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
